import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { referralsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import {
    ArrowLeft,
    BadgePercent,
    Check,
    Copy,
    Gift,
    Mail,
    MessageCircle,
    Send,
    Share2,
    Sparkles,
    Twitter,
    Users,
} from "lucide-react";
import { toast } from "sonner";

const ReferPage = () => {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await referralsAPI.me();
            setData(r.data);
        } catch (e) {
            // Non-critical — page falls back to disabled state
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const pct = data?.discount_percent || 0;
    // Use the backend /api/r/{code} shim so iMessage/WhatsApp/X/Slack render a
    // branded preview card. Regular browsers are instantly redirected to /?ref={code}.
    const shareUrl = data?.code ? `${window.location.origin}/api/r/${data.code}` : "";
    const shareText = data
        ? `I'm using CreditFix AI to clean up my credit report — sign up with my link and you'll get ${pct}% off your plan: ${shareUrl}`
        : "";

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link copied to clipboard");
            setTimeout(() => setCopied(false), 2200);
        } catch {
            toast.error("Could not copy — long-press the link instead.");
        }
    };

    const shareNative = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "CreditFix AI",
                    text: `Get ${pct}% off CreditFix AI`,
                    url: shareUrl,
                });
            } catch (e) {
                if (e.name !== "AbortError") copyLink();
            }
        } else {
            copyLink();
        }
    };

    const intentLinks = data ? [
        {
            id: "sms",
            label: "Text",
            href: `sms:?&body=${encodeURIComponent(shareText)}`,
            icon: MessageCircle,
        },
        {
            id: "email",
            label: "Email",
            href: `mailto:?subject=${encodeURIComponent(`Get ${pct}% off CreditFix AI`)}&body=${encodeURIComponent(shareText)}`,
            icon: Mail,
        },
        {
            id: "x",
            label: "X / Twitter",
            href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
            icon: Twitter,
        },
        {
            id: "whatsapp",
            label: "WhatsApp",
            href: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
            icon: Send,
        },
    ] : [];

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
            </div>
        );
    }

    // Program disabled by admin — show graceful empty state
    const programOff = !data?.program_enabled || pct <= 0;

    return (
        <div className="min-h-screen bg-background">
            {/* Slim header */}
            <header className="border-b border-border/60 bg-background/80 backdrop-blur sticky top-0 z-40">
                <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
                    <Link to="/dashboard" className="flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity" data-testid="refer-back-btn">
                        <ArrowLeft className="h-4 w-4" />
                        Dashboard
                    </Link>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                        Refer · Earn
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16">
                {programOff ? (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center py-16"
                        data-testid="refer-disabled"
                    >
                        <Gift className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
                        <h1 className="font-serif text-2xl mb-2">Referral program is paused</h1>
                        <p className="text-muted-foreground text-sm max-w-md mx-auto">
                            We're not running referrals right now. Check back soon — or reach out to support if you think this is a mistake.
                        </p>
                        <Link to="/dashboard">
                            <Button variant="outline" className="rounded-full mt-6">Back to dashboard</Button>
                        </Link>
                    </motion.div>
                ) : (
                    <>
                        {/* Hero */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="mb-10"
                        >
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider mb-5">
                                <Sparkles className="h-3 w-3" />
                                Limited program
                            </div>
                            <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight mb-4">
                                Give <span className="text-primary">{pct}% off.</span><br />
                                Get <span className="text-accent">{pct}% off.</span>
                            </h1>
                            <p className="text-muted-foreground text-lg max-w-xl leading-relaxed">
                                Send your link. When a friend signs up with it, they get <span className="font-semibold text-foreground">{pct}% off their first plan</span> — and so do you. No caps. No expiry.
                            </p>
                        </motion.div>

                        {/* Link card */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.08 }}
                        >
                            <Card className="border-2 border-foreground/10 shadow-sm overflow-hidden">
                                <CardContent className="p-6 md:p-8">
                                    <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                                        <BadgePercent className="h-3.5 w-3.5" />
                                        Your share link
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-2">
                                        <Input
                                            value={shareUrl}
                                            readOnly
                                            onFocus={(e) => e.target.select()}
                                            className="font-mono text-sm h-12"
                                            data-testid="refer-link-input"
                                        />
                                        <Button
                                            onClick={copyLink}
                                            variant="outline"
                                            className="h-12 px-5 rounded-md flex-shrink-0 btn-press"
                                            data-testid="refer-copy-btn"
                                        >
                                            {copied ? (
                                                <><Check className="h-4 w-4 mr-2 text-accent" /> Copied</>
                                            ) : (
                                                <><Copy className="h-4 w-4 mr-2" /> Copy</>
                                            )}
                                        </Button>
                                        <Button
                                            onClick={shareNative}
                                            className="h-12 px-5 rounded-md flex-shrink-0 btn-press"
                                            data-testid="refer-share-btn"
                                        >
                                            <Share2 className="h-4 w-4 mr-2" />
                                            Share
                                        </Button>
                                    </div>

                                    <div className="text-[11px] text-muted-foreground mt-3 font-mono tracking-wider">
                                        Code:&nbsp;<span className="font-semibold text-foreground" data-testid="refer-code">{data.code}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>

                        {/* Live preview of how the link looks when shared (iMessage / WhatsApp / X) */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.12 }}
                            className="mt-6"
                        >
                            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                                <Sparkles className="h-3.5 w-3.5" />
                                Preview when shared
                            </div>
                            <div className="rounded-2xl border border-border/60 bg-muted/30 p-4 md:p-5 max-w-md">
                                <div className="rounded-xl overflow-hidden bg-background border border-border/60 shadow-sm">
                                    <img
                                        src={`/api/og/referral.png?code=${data.code}`}
                                        alt={`How your link previews — ${data.referrer_first_name || ""} sent you ${pct}% off`}
                                        className="w-full aspect-[1200/630] object-cover"
                                        loading="lazy"
                                        data-testid="refer-og-preview"
                                    />
                                    <div className="px-3 py-2 text-[11px] text-muted-foreground">
                                        <p className="font-medium text-foreground truncate" data-testid="refer-og-preview-title">
                                            {data.code} · {pct}% off CreditFix AI
                                        </p>
                                        <p className="truncate">aifixedmycredit.com</p>
                                    </div>
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                                    This is exactly what friends will see when you paste your link into iMessage, WhatsApp, X, or Slack.
                                </p>
                            </div>
                        </motion.div>

                        {/* Quick-share intents */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.16 }}
                            className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4"
                            data-testid="refer-intents"
                        >
                            {intentLinks.map((link) => (
                                <a
                                    key={link.id}
                                    href={link.href}
                                    target={link.id === "x" || link.id === "whatsapp" ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    className="group flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl border border-border bg-background hover:border-foreground/30 hover:bg-muted/30 transition-all text-sm font-medium"
                                    data-testid={`refer-intent-${link.id}`}
                                >
                                    <link.icon className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                                    {link.label}
                                </a>
                            ))}
                        </motion.div>

                        {/* Stats — clean horizontal strip */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.24 }}
                            className="mt-10 grid grid-cols-3 gap-px bg-border/60 rounded-2xl overflow-hidden border border-border/60"
                        >
                            <div className="bg-background p-5 md:p-6 text-center" data-testid="refer-stat-invited">
                                <div className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                                    <Users className="h-3 w-3" /> Invited
                                </div>
                                <p className="text-3xl md:text-4xl font-bold font-mono">{data.invited}</p>
                            </div>
                            <div className="bg-background p-5 md:p-6 text-center" data-testid="refer-stat-joined">
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Joined</div>
                                <p className="text-3xl md:text-4xl font-bold font-mono text-accent">{data.converted}</p>
                            </div>
                            <div className="bg-background p-5 md:p-6 text-center" data-testid="refer-stat-yours">
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Your discount</div>
                                <p className={`text-3xl md:text-4xl font-bold font-mono ${data.has_pending_discount ? "text-primary" : "text-muted-foreground/40"}`}>
                                    {data.has_pending_discount ? `${pct}%` : "—"}
                                </p>
                            </div>
                        </motion.div>

                        {data.has_pending_discount && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="mt-4 flex items-center justify-center gap-2 text-sm text-accent font-medium"
                                data-testid="refer-pending-banner"
                            >
                                <Sparkles className="h-3.5 w-3.5" />
                                Your {pct}% discount applies automatically at your next checkout.
                            </motion.div>
                        )}

                        {/* How it works */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.32 }}
                            className="mt-14"
                        >
                            <h2 className="font-serif text-xl mb-5 text-muted-foreground">How it works</h2>
                            <ol className="space-y-4">
                                {[
                                    { n: "01", t: "Share your link", d: "Text it, post it, email it. The link is uniquely yours." },
                                    { n: "02", t: "They get the discount", d: `Your friend lands on a page that already shows them their ${pct}% off. They sign up and the discount auto-applies.` },
                                    { n: "03", t: "You get yours", d: `The moment they pay, your ${pct}% discount is locked in for your next plan or renewal.` },
                                ].map((s) => (
                                    <li key={s.n} className="flex gap-5 items-start group">
                                        <div className="text-xs font-mono font-bold text-primary tracking-widest pt-1 w-8 flex-shrink-0">
                                            {s.n}
                                        </div>
                                        <div className="flex-1 border-l border-border/60 pl-5 pb-4">
                                            <p className="font-semibold text-base mb-1">{s.t}</p>
                                            <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </motion.div>

                        {/* Fine print */}
                        <p className="text-[11px] text-muted-foreground/70 mt-12 leading-relaxed max-w-2xl">
                            Discounts apply to a single paid checkout (one-time access or the first invoice of a monthly subscription) and are consumed automatically. The referral program may be paused or modified at any time. Self-referrals don't count.
                        </p>
                    </>
                )}
            </main>
        </div>
    );
};

export default ReferPage;
